export type OfferType = {
  id: number;
  productId: number;
  fromUserId: number;
  toUserId: number;
  status: OfferStatus;
  statusReason: OfferStatusReason;
  oldPrice: number;
  offeredPrice: number;
  oldOfferId?: number;
  createdAt: string;
  updatedAt: string;
  respondedAt: string;
  expiresAt?: string | null;
  expiryVariationId?: string | null;
};

export const OfferStatusOption = {
  PENDING: 'pending',
  CANCELLED: 'cancelled',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  COMPLETED: 'completed',
  EXPIRED: 'expired',
} as const;

export type OfferStatus = (typeof OfferStatusOption)[keyof typeof OfferStatusOption];

export const OfferStatusReasonOption = {
  OFFERED_BY_SELLER: 'offered_by_seller',
  OFFERED_BY_BUYER: 'offered_by_buyer',
  AUTO_CANCELLED_PRICE_LOWERED: 'auto_cancelled_price_lowered',
  AUTO_CANCELLED_ITEM_SOLD: 'auto_cancelled_item_sold',
  AUTO_CANCELLED_ITEM_DELETED: 'auto_cancelled_item_deleted',
  AUTO_CANCELLED_ITEM_UNAVAILABLE: 'auto_cancelled_item_unavailable',
  CANCELLED_BY_SELLER: 'cancelled_by_seller',
  CANCELLED_PRICE_RAISED: 'cancelled_price_raised',
  DECLINED_BY_BUYER: 'declined_by_buyer',
  DECLINED_BY_SELLER: 'declined_by_seller',
  ACCEPTED_BY_SELLER: 'accepted_by_seller',
  ACCEPTED_BY_BUYER: 'accepted_by_buyer',
  EXPIRED_UNPAID: 'expired_unpaid',
} as const;

export type OfferStatusReason = (typeof OfferStatusReasonOption)[keyof typeof OfferStatusReasonOption];

export const OfferStatusMessageOption = {
  ...OfferStatusOption,
  COUNTER_PENDING: 'counter_pending',
  SELLER_PENDING: 'seller_pending',
  TOO_HIGH_AUTO_CANCELLED: 'too_high_auto_cancelled',
  EXPIRY_REMINDER_FIRST: 'expiry_reminder_first',
  EXPIRY_REMINDER_LAST: 'expiry_reminder_last',
  ITEM_SOLD_AUTO_CANCELLED: 'item_sold_auto_cancelled',
  ITEM_DELETED_AUTO_CANCELLED: 'item_deleted_auto_cancelled',
  ITEM_UNAVAILABLE_AUTO_CANCELLED: 'item_unavailable_auto_cancelled',
  PRICE_RAISED_CANCELLED: 'price_raised_cancelled',
} as const;

export type OfferStatusMessage = (typeof OfferStatusMessageOption)[keyof typeof OfferStatusMessageOption];

/** DEV-2443: an accepted offer past its deadline is expired even before the hourly job flips its status. */
/** Products with a pending, or accepted and still valid, offer: a new offer cannot be made on them. */
export const openOfferProductIds = (offers: (OfferType | null | undefined)[]) =>
  new Set(
    offers
      .filter(
        (offer) => offer?.status === OfferStatusOption.PENDING || (offer?.status === OfferStatusOption.ACCEPTED && !isOfferExpired(offer)),
      )
      .map((offer) => offer.productId),
  );

export const isOfferExpired = (offer?: Pick<OfferType, 'status' | 'expiresAt'> | null) =>
  offer?.status === OfferStatusOption.EXPIRED ||
  (offer?.status === OfferStatusOption.ACCEPTED && !!offer.expiresAt && new Date(offer.expiresAt).getTime() <= Date.now());
